import { useState } from "react";

import { App, Divider, theme, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import FormField from "../../../../components/form/FormField.jsx";
import Input from "../../../../components/form/Input.jsx";
import { setAuthenticationState } from "../../../../redux/authentication/reducer.ts";
import { LocalStorageName } from "@/redux/api.ts";
import { handleErrors } from "@/shared/utils/forms.js";
import PrimaryButton from "../../../../components/PrimaryButton.jsx";
import DeleteConfirmationModel from "../../../../components/DeleteConfirmationModel.jsx";
import {
  useDeleteUserByIdMutation,
  useUpdateUserByIdMutation,
} from "../../../../redux/service.js";
import BaseButton from "@/components_v2/Button/BaseButton.js";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.js";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import ExportIcon from "@/assets/icons/export.svg?react";
import { useUserExportDownload } from "@/features/users/settings/hooks/useUserExportDownload.js";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";

const { Title, Text } = Typography;

const ChangePasswordForm = ({ userId }) => {
  const { token } = theme.useToken();
  const [updateUser, { isLoading }] = useUpdateUserByIdMutation();
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const changePasswordSchema = yup.object().shape({
    oldPassword: yup
      .string()
      .typeError(t("form.validation.oldPasswordNotValid"))
      .required(t("form.validation.oldPasswordRequired")),
    newPassword: yup
      .string()
      .typeError(t("form.validation.newPasswordNotValid"))
      .min(8, t("form.validation.passwordLengthValidation"))
      .matches(/[A-Z]/, t("form.validation.passwordCapitalLetterValidation"))
      .matches(/[0-9]/, t("form.validation.passwordNumberValidation"))
      .required(t("form.validation.newPasswordRequired")),
    confirmPassword: yup
      .string()
      .typeError(t("form.validation.passwordNotValid"))
      .oneOf(
        [yup.ref("newPassword"), null],
        t("form.validation.passwordsMustMatch"),
      )
      .required(t("form.validation.passwordRequired")),
  });

  const form = useForm({
    resolver: yupResolver(changePasswordSchema),
    mode: "onChange",
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = (values) => {
    updateUser({ id: userId, data: values })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("messages.passwordChange.successMessage"),
        });
        form.reset();
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Title level={4} style={{ color: token.colorPrimaryText }}>
        {t("titles.changePassword")}
      </Title>
      <FormField
        label={t("form.labels.oldPassword")}
        field={
          <Input
            name="oldPassword"
            htmlType="password"
            control={form.control}
          />
        }
      />
      <FormField
        label={t("form.labels.newPassword")}
        field={
          <Input
            name="newPassword"
            htmlType="password"
            control={form.control}
          />
        }
      />
      <FormField
        label={t("form.labels.confirmPassword")}
        field={
          <Input
            name="confirmPassword"
            htmlType="password"
            control={form.control}
          />
        }
      />
      <PrimaryButton htmlType="submit" disabled={isLoading} className="mt-3">
        {t("buttons.updatePassword")}
      </PrimaryButton>
    </form>
  );
};

const ChangeEmailForm = ({ userId }) => {
  const { token } = theme.useToken();
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [updateUser, { isLoading }] = useUpdateUserByIdMutation();

  const changeEmailSchema = yup.object().shape({
    newEmail: yup
      .string()
      .typeError(t("form.validation.emailNotValid"))
      .email(t("form.validation.emailNotValid"))
      .required(t("form.validation.emailRequired")),
    password: yup
      .string()
      .typeError(t("form.validation.passwordNotValid"))
      .min(8, t("form.validation.passwordLengthValidation"))
      .matches(/[A-Z]/, t("form.validation.passwordCapitalLetterValidation"))
      .matches(/[0-9]/, t("form.validation.passwordNumberValidation"))
      .required(t("form.validation.passwordRequired")),
  });

  const form = useForm({
    resolver: yupResolver(changeEmailSchema),
    mode: "onTouched",
  });

  const onSubmit = (values) => {
    updateUser({ id: userId, data: values })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("messages.emailChangedAndVerificationEmail"),
        });
        dispatch(
          setAuthenticationState({ isAuthenticated: false, user: null }),
        );
        window.localStorage.removeItem(LocalStorageName);
        navigate("/login");
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Title level={4} style={{ color: token.colorPrimaryText }}>
        Change email
      </Title>
      <FormField
        label={t("form.labels.password")}
        field={
          <Input name="password" htmlType="password" control={form.control} />
        }
      />
      <FormField
        label={t("form.labels.newEmail")}
        field={
          <Input name="newEmail" htmlType="email" control={form.control} />
        }
      />
      <PrimaryButton htmlType="submit" disabled={isLoading} className="mt-3">
        {t("buttons.updateEmail")}
      </PrimaryButton>
    </form>
  );
};

const AccountSettings = () => {
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { startDownload, isDownloading } = useUserExportDownload();

  const deleteUserSchema = yup.object().shape({
    password: yup
      .string()
      .typeError(t("form.validation.passwordNotValid"))
      .min(8, t("form.validation.passwordLengthValidation"))
      .matches(/[A-Z]/, t("form.validation.passwordCapitalLetterValidation"))
      .matches(/[0-9]/, t("form.validation.passwordNumberValidation"))
      .required(t("form.validation.passwordRequired")),
  });

  const form = useForm({
    resolver: yupResolver(deleteUserSchema),
    mode: "onChange",
  });
  const [deleteUser, { isLoading }] = useDeleteUserByIdMutation();
  const currentUser = useSelector(selectCurrentUser);
  const [open, setOpen] = useState(false);

  const handleDelete = async () => {
    await form.trigger();
    deleteUser({ id: currentUser.id, data: form.getValues() })
      .unwrap()
      .then(() => {
        notification.success({ message: t("accountDeleted") });
        dispatch(setAuthenticationState({ isAuthenticated: false }));
        window.localStorage.removeItem(LocalStorageName);
        navigate("/login");
      })
      .catch((errors) => {
        const error = errors?.data?.errors[0];
        if (error?.code === "delete_last_user") {
          notification.error({ message: t(`errors.${error.code}`) });
        } else {
          handleErrors(errors, form.setError, notification);
        }
      });
  };

  return (
    <div>
      <div>
        <ChangePasswordForm userId={currentUser?.id} />
        <Divider />
      </div>
      <div>
        <ChangeEmailForm userId={currentUser?.id} />
        <Divider />
      </div>
      <div>
        <Title level={4}>{t("deleteAccount")}</Title>
        <PrimaryButton className="mt-2" onClick={() => setOpen(true)}>
          {t("buttons.deleteAccount")}
        </PrimaryButton>
        <DeleteConfirmationModel
          isLoading={isLoading}
          onSubmit={handleDelete}
          title={t("confirm.deleteUserConfirmationTitle")}
          onCancel={() => setOpen(false)}
          open={open}
        >
          <div>
            <Text>{t("confirm.deleteUserConfirmationContent1")}</Text>
            <FormField
              label={t("form.labels.password")}
              field={
                <Input
                  name="password"
                  htmlType="password"
                  control={form.control}
                />
              }
            />
          </div>
        </DeleteConfirmationModel>
      </div>
      <div className="mt-3">
        {currentUser && (
          <div>
            <Title level={4}>{t("retrieveData")}</Title>
            <BaseButton
              onClick={() =>
                startDownload(currentUser.id, currentUser.fullName)
              }
              disabled={isDownloading(currentUser.id)}
              leadingIcon={
                isDownloading(currentUser.id) ? (
                  <BaseSpinner size="small" />
                ) : (
                  <BaseSvgRenderer
                    component={ExportIcon}
                    width="20"
                    height="20"
                    stroke="white"
                  />
                )
              }
            >
              {t("buttons.export")}
            </BaseButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;
